// packages/api/src/PublicLostFoundService.ts
//
// ROADMAP #68 — the PUBLIC lost & found client: what the Lost & found block shows this visitor (`publicLostFound`), what
// this browser reported (`publicLostFoundMine`), and the two reports — "I lost something" (`publicLostReport`) and "I found
// something" (`publicFoundReport`, which answers with the code the finder shows at the desk). Anonymous, published-only,
// rate-limited; mirrors eventsmagic-api PublicLostFoundCore.
//
// Runs through the injected client (see `configureApiService`) so it works in the BROWSER.
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";

/** #68 — the lost & found desk as the site shows it: its name, the settings' note, and the point's own details. */
export interface LostFoundDesk {
  name?: string | null;
  note?: string | null;
  phone?: string | null;
  howToFind?: string | null;
  address?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  photoUploadedFile?: { fileUrl?: string | null; thumbnailUrl?: string | null } | null;
}

export interface PublicLostFoundContact {
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
}

interface PublicLostFoundReportBase {
  url: string;
  /** ⚠ A binding write: pass `ensureVisitorId()`, never a possibly-null read. */
  visitorId: string;
  eventTimeSlotId?: string | null;
  category: string;
  description: string;
  latitude?: number | null;
  longitude?: number | null;
  locationAccuracyM?: number | null;
  /** Required when the visitor is not a recognised guest. */
  contact?: PublicLostFoundContact | null;
}

export interface PublicLostReportInput extends PublicLostFoundReportBase {
  lostWhere?: string | null;
  lostAt?: string | null;
}

export interface PublicFoundReportInput extends PublicLostFoundReportBase {
  foundWhere?: string | null;
  foundAt?: string | null;
  /** Up to 3 photos, JPEG base64, scaled down before sending. */
  photos?: string[];
}

export interface PublicLostFoundReceipt {
  sequenceNo?: number | null;
  /** Found only: what the finder shows at the desk, as a QR. */
  code?: string | null;
  desk?: LostFoundDesk | null;
  photosSaved?: number | null;
  photosFailed?: number | null;
}

export interface PublicLostReportMine {
  id: string;
  sequenceNo?: number | null;
  category?: string | null;
  description?: string | null;
  reportState?: string | null;
  closedReason?: string | null;
  createdAt?: string | null;
}

export interface PublicFoundItemMine {
  id: string;
  sequenceNo?: number | null;
  code?: string | null;
  category?: string | null;
  itemState?: string | null;
  createdAt?: string | null;
}

export interface PublicLostFoundMine {
  lost?: PublicLostReportMine[] | null;
  found?: PublicFoundItemMine[] | null;
}

const FORM_FIELDS = `allowed needsContact guestsOnly`;

const PUBLIC_LOST_FOUND_QUERY = gql`
  query PublicLostFound($params: PublicLostFoundInput) {
    publicLostFound(params: $params) {
      code
      errors { name errors }
      data {
        open
        isGuest
        reportLost { ${FORM_FIELDS} }
        reportFound { ${FORM_FIELDS} }
        showList
        desk { name note phone howToFind address latitude longitude photoUploadedFile { fileUrl thumbnailUrl } }
        categories
        items { sequenceNo category description foundOn foundAt foundNear collectAt }
        itemsTotal
      }
    }
  }
`;

const PUBLIC_LOST_FOUND_MINE_QUERY = gql`
  query PublicLostFoundMine($params: PublicLostFoundInput) {
    publicLostFoundMine(params: $params) {
      code
      errors { name errors }
      data {
        lost { id sequenceNo category description reportState closedReason createdAt }
        found { id sequenceNo code category itemState createdAt }
      }
    }
  }
`;

const RECEIPT = `
      code
      errors { name errors }
      data { sequenceNo code desk { name note phone howToFind address latitude longitude photoUploadedFile { fileUrl thumbnailUrl } } photosSaved photosFailed }`;

const PUBLIC_LOST_REPORT_MUTATION = gql`
  mutation PublicLostReport($params: PublicLostReportInput) {
    publicLostReport(params: $params) {${RECEIPT}
    }
  }
`;

const PUBLIC_FOUND_REPORT_MUTATION = gql`
  mutation PublicFoundReport($params: PublicFoundReportInput) {
    publicFoundReport(params: $params) {${RECEIPT}
    }
  }
`;

export class PublicLostFoundService {
  /** What the Lost & found block shows this visitor. Always a fresh network read (the list and the gates change). */
  static async info(args: { url: string; visitorId?: string | null; eventTimeSlotId?: string | null }, options: Record<string, any> = {}): Promise<OpResult> {
    return ApiService.executeQuery(
      PUBLIC_LOST_FOUND_QUERY,
      { params: { url: args.url, visitorId: args.visitorId ?? null, eventTimeSlotId: args.eventTimeSlotId ?? null } },
      { fetchPolicy: "network-only", ...options }
    );
  }

  /** What this browser reported on the event (a wrong / absent visitor id gets an empty answer). */
  static async mine(args: { url: string; visitorId?: string | null }, options: Record<string, any> = {}): Promise<OpResult> {
    return ApiService.executeQuery(
      PUBLIC_LOST_FOUND_MINE_QUERY,
      { params: { url: args.url, visitorId: args.visitorId ?? null } },
      { fetchPolicy: "network-only", ...options }
    );
  }

  static async reportLost(input: PublicLostReportInput, options: Record<string, any> = {}): Promise<OpResult> {
    return ApiService.executeMutation(PUBLIC_LOST_REPORT_MUTATION, { params: input }, options);
  }

  static async reportFound(input: PublicFoundReportInput, options: Record<string, any> = {}): Promise<OpResult> {
    return ApiService.executeMutation(PUBLIC_FOUND_REPORT_MUTATION, { params: input }, options);
  }
}
